import RepetirExame from '@/components/medico/repetir-exame/index.vue'
import CategoryMixin from '@/shared/mixins/category.mixin'
import ReportCountdown from '@/components/medico/report-countdown/index.vue'
import PhysicianExamCounters from '@/components/medico/physician-exam-counters/index.vue'
import SlaDeadlineCountdown from '@/components/medico/sla-deadline-countdown/index.vue'
import DateMixin from '@/shared/mixins/date.mixin'
import RoleMixin from '@/shared/mixins/role.mixin'
import AuthMixin from '@/shared/mixins/auth.mixin'
import MedicalReportMixin from '@/shared/mixins/medical-report.mixin'
import ExamInformationMixin from '@/shared/mixins/exam-information.mixin'
import ExamUrgencyAlertMixin from '@/shared/mixins/exam-urgency-alert.mixin'
import CONSTANTS from '@/shared/constants/constants'
import ExamDetailsAlert from '@/components/exames/exams-details/exam-details-alert/index.vue'
import TokenCountdown from '@/components/medico/token-countdown/index.vue'
import {
  filterExamPanelLabels,
  resolveExamPanelLabelClassName
} from '@/shared/utils/exam-detail-labels'

const emptyCounter = () => ({
  queue: 0,
  available: 0,
  delayed: 0
})

const unavailableCounter = () => ({
  queue: '-',
  available: '-',
  delayed: '-'
})

export default {
  name: 'medico-header',
  components: {
    RepetirExame,
    ReportCountdown,
    PhysicianExamCounters,
    SlaDeadlineCountdown,
    ExamDetailsAlert,
    TokenCountdown
  },
  mixins: [
    CategoryMixin,
    DateMixin,
    ExamInformationMixin,
    ExamUrgencyAlertMixin,
    RoleMixin,
    AuthMixin,
    MedicalReportMixin
  ],
  props: {
    exam: {
      type: Object,
      default: {}
    },
    hasCountdown: {
      type: Boolean,
      default: true
    },
    hasInformation: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      creationTime: Object,
      performLabel: '',
      categorySvg: '',
      pixeonUrl: import.meta.env.VITE_URL_PIXEON,
      informations: [],
      roles: [],
      instructionText: this.$i18n.t('add_here_questions_related'),
      isOit: false,
      isCategoryXray: false,
      isCanAlertUrgency: false,
      token: false,
      tokenExpiration: '',
      usePtmSigner: false,
      showTokenExpiredModal: false,
      isOpeningXView: false,
      qrCodeUrl: '',
      physicianCountersLoading: false,
      physicianElectiveCounter: emptyCounter()
    }
  },
  computed: {
    examDeadline () {
      return this.exam?.deadline || null
    },
    examOpeningDate () {
      return this.exam?.creationDate || null
    },
    currentUser () {
      const user = this.getUser()
      const currentRole = this.roles.find((role) => {
        return user.role.name === role.name
      })

      if (!currentRole) {
        return {}
      }

      return {
        name: user.fullname,
        role: currentRole.label
      }
    },
    formattedInformations () {
      return this.informations.map((information) => {
        const role = this.roles.find((role) => {
          return information.role === role.name
        })

        information.role = role.label
        information.creationDate = this.getFormattedDateHour(information.creationDate)
        return information
      })
    },
    hasTimelineAccess () {
      const user = this.getUser()
      return user.role.name === 'physician' && user.physician && !user.physician.onlyReports
    },
    shouldShowDicomButtons () {
      const allowedCategories = [1, 6, 8, 9, 10]
      return allowedCategories.includes(this.exam?.categorySpecialtyId)
    },
    examPanelLabels () {
      return filterExamPanelLabels(this.exam && this.exam.labels)
    }
  },
  async mounted () {
    this.$root.$emit('showLoading')
    try {
      await this.load()
      if (this.hasCountdown) {
        this.refreshPhysicianCounters()
      }

      const { cert, token } = JSON.parse(sessionStorage.getItem('certificateValidation')) || { cert: false, token: false } // Default to false if not found

      const requests = [
        this.getRoles(),
        this.getInformationsByExamId(this.exam.examId),
        this.getCanAlertBySource(this.exam.examId)
      ]

      if (cert && token) {
        requests.push(this.getValidateCertificate())
      }

      const res = await Promise.all(requests)

      this.roles = res[0]
      this.informations = res[1].data
      this.isOit = this.exam.specialityId === CONSTANTS.SPECIALITY.RAIOXOIT
      this.isCategoryXray = this.exam.categorySpecialtyId === CONSTANTS.CATEGORY.XRAY
      this.isCanAlertUrgency = res[2]

      if (cert && token && res.length > 3) {
        this.usePtmSigner = res[3].usePtmSigner
        this.token = res[3].token
        this.qrCodeUrl = res[3].qrCodeUrl || ''

        const tokenExpiration = res[3].tokenExpiration
        if (tokenExpiration) {
          this.tokenExpiration =
            tokenExpiration instanceof Date ? tokenExpiration.toISOString() : new Date(tokenExpiration).toISOString()
        } else {
          this.tokenExpiration = ''
        }
      } else {
        this.qrCodeUrl = ''
      }
    } finally {
      this.$root.$emit('hideLoading')
    }
  },
  methods: {
    resolveExamPanelLabelClassName,
    async load () {
      this.creationTime = this.getDateDifferenceDays()
      this.performLabel = this.$i18n.tc(`time.${this.creationTime.label}`, this.creationTime.value)
      this.categorySvg = await this.getIconByCategoryForMedicHeader(this.exam)

      if (this.exam.pixeonUid) {
        this.pixeonUrl += this.exam.pixeonUid
      }
    },
    getDateDifferenceDays () {
      const initialDate = new Date(this.exam.creationDate)
      const today = new Date()
      const days = this.toDays(this.toUTC(today) - this.toUTC(initialDate))
      if (days) {
        return {
          value: days,
          label: 'day'
        }
      }

      const hours = today.getUTCHours() - initialDate.getUTCHours()
      if (hours) {
        return {
          value: hours,
          label: 'hour'
        }
      }

      const minutes = today.getUTCMinutes() - initialDate.getUTCMinutes()
      if (minutes) {
        return {
          value: minutes,
          label: 'minute'
        }
      }

      const seconds = today.getUTCSeconds() - initialDate.getUTCSeconds()
      return {
        value: seconds,
        label: 'second'
      }
    },
    toDays (date) {
      date = date || 0
      return date / 24 / 60 / 60 / 1000
    },
    toUTC (date) {
      if (!date || !date.getFullYear) {
        return 0
      }
      return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    },
    repeatExam (data) {
      this.$emit('repeatExam', data)
    },
    unassignExam (data) {
      this.$emit('unassignExam', data)
    },
    restartCountDown () {
      if (this.hasCountdown) {
        this.$refs.reportCountDown.restartCountDown()
      }
    },
    stopCountDown () {
      if (this.hasCountdown) {
        this.$refs.reportCountDown.stopCountDown()
      }
    },
    getClassButtons () {
      return {
        'repeat-exam-2-buttons': this.exam.pixeonUid
      }
    },
    hasSendInformation () {
      const user = this.getUser()
      return user.role.name === 'physician'
    },
    async clickSendInformation (information) {
      this.$root.$emit('showLoading')
      try {
        await this.sendInformation(this.exam.examId, information)

        this.stopCountDown()
        const res = await this.hasMoreExams()
        const hasMoreExams = res.data
        this.$emit('sendInformation', {
          hasMoreExams,
          type: 'sendInformation'
        })
        this.$refs.information.hide()
      } finally {
        this.$root.$emit('hideLoading')
      }
    },
    showFile () {
      const file = this.exam.examFiles[0]
      const route = this.$router.resolve({ name: 'examesPdf', query: { id: file.examFileId, isLaudo: false } })
      window.open(route.href, '_blank')
    },
    handleTokenExpired () {
      if (this.usePtmSigner) {
        sessionStorage.removeItem('certificateValidation')
        sessionStorage.removeItem('isValidatedPhysician')
        this.showTokenExpiredModal = true
      }
    },
    handleModalConfirm () {
      this.showTokenExpiredModal = false
      this.logoutUser()
    },
    revalidateToken () {
      this.redirectToQrCodeValidation(this.qrCodeUrl)
    },
    async handleOpenXView() {
      if (this.isOpeningXView) return
      this.isOpeningXView = true

      const popup = window.open('about:blank', '_blank')

      this.$root.$emit('showLoading')
      try {
        const result = await this.getExamXView(this.exam.examId)
        const examURL = result?.exameURL
        const specialtyId = this.exam?.categorySpecialtyId

        // ESTUDO GTM + GA4 PARA APENAS AS ESPECIALIDADES DE RADIO
        const allowedSpecialties = [1, 6, 9, 10]

        if (allowedSpecialties.includes(specialtyId)) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'click_xview',
            click_text: 'Ver na Web',
            click_url: examURL || '',
            button_id: 'xview',
            specialty_id: specialtyId,
            page_path: window.location.pathname
          })
        }
        // ABAIXO SEGUE FLUXO NORMAL
        if (!examURL) {
          if (popup) popup.close()
          this.$toast.error(this.$t('no_exams'))
          return
        }
        if (popup) {
          popup.location.href = examURL
        } else {
          window.location.assign(examURL)
        }
      } catch (err) {
        if (popup) popup.close()
        this.$toast.error(this.$t('unexpected_error_occurred'))
      } finally {
        this.$root.$emit('hideLoading')
        this.isOpeningXView = false
      }
    },
    handleOpenPixeon() {
      const specialtyId = this.exam?.categorySpecialtyId

      // ESTUDO GTM + GA4 PARA APENAS AS ESPECIALIDADES DE RADIO
      const allowedSpecialties = [1, 6, 9, 10]

      if (allowedSpecialties.includes(specialtyId)) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'click_pixeon',
          click_text: 'Ver no PACS',
          click_url: this.pixeonUrl || '',
          button_id: 'pixeon',
          specialty_id: specialtyId,
          page_path: window.location.pathname
        })
      }
      // ABAIXO SEGUE FLUXO NORMAL
      if (this.pixeonUrl) {
        window.location.href = this.pixeonUrl
      } else {
        this.$toast.error(this.$t('no_exams'))
      }
    },
    clearOitFields () {
      this.$emit('clearOitFields')
    },
    async refreshPhysicianCounters () {
      this.physicianCountersLoading = true
      try {
        const response = await this.getPhysicianCounters()
        const elective = response.data.counters.find((counter) => counter.name === 'Elective')

        this.physicianElectiveCounter = elective
          ? {
            queue: elective.queue,
            available: elective.available,
            delayed: elective.delayed
          }
          : emptyCounter()
      } catch (error) {
        this.physicianElectiveCounter = unavailableCounter()
        console.error('Erro ao carregar contadores do médico:', error)
      } finally {
        this.physicianCountersLoading = false
      }
    }
  }
}
